import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { Search, School, X, Users } from "lucide-react";
import {
  getTeacherAccounts,
  deleteTeacherAccount,
  resetTeacherAccountPassword,
} from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import { grantDocumentAccessBonus } from "../../../api/elearning/documents/documentAccess";
import {
  showConfirmDialog,
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import Pagination from "../../../components/common/Pagination";
import TeacherAccountRowActionMenu from "./TeacherAccountRowActionMenu";
import TeacherDeviceManageModal from "./TeacherDeviceManageModal";
import TeacherAccountDetailModal from "./TeacherAccountDetailModal";

/**
 * Popup danh sách GV của một trường + menu thao tác trên từng dòng.
 */
const TeachersBySchoolModal = ({
  isOpen,
  school,
  statusFilter = "",
  schoolLevelFilter = "",
  refreshVersion = 0,
  onClose,
  onEdit,
  onChanged,
}) => {
  const [items, setItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword] = useDebounce(keyword, 350);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [deviceTeacher, setDeviceTeacher] = useState(null);
  const [detailTeacherId, setDetailTeacherId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState("account");

  useLayoutEffect(() => {
    if (!isOpen) return;
    setKeyword("");
    setCurrentPage(1);
    setOpenActionMenuId(null);
    setDeviceTeacher(null);
    setDetailTeacherId(null);
  }, [isOpen, school?.partner_customer_id]);

  const fetchTeachers = useCallback(async () => {
    if (!isOpen || !school?.partner_customer_id) return;
    try {
      setLoading(true);
      const res = await getTeacherAccounts({
        customer_id: school.partner_customer_id,
        keyword: debouncedKeyword || undefined,
        status: statusFilter || undefined,
        school_level: schoolLevelFilter || undefined,
        page: currentPage,
        pageSize,
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotalRecords(Number(res?.total ?? 0));
    } catch (e) {
      console.error(e);
      showErrorToast("Không tải được danh sách giáo viên.");
      setItems([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    isOpen,
    school?.partner_customer_id,
    debouncedKeyword,
    statusFilter,
    schoolLevelFilter,
    currentPage,
    pageSize,
    refreshVersion,
  ]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useLayoutEffect(() => {
    setCurrentPage(1);
  }, [debouncedKeyword]);

  const notifyChanged = () => {
    onChanged?.();
    fetchTeachers();
  };

  const handleResetPassword = async (teacher) => {
    const confirm = await showConfirmDialog({
      title: "Reset mật khẩu?",
      text: `Hệ thống sẽ tạo mật khẩu tạm mới và gửi email tới ${teacher.email || teacher.name}.`,
      confirmText: "Reset mật khẩu",
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = await resetTeacherAccountPassword(teacher.id);
      showSuccessToast(res?.message || "Đã reset mật khẩu và gửi email.");
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không reset được mật khẩu.");
    }
  };

  const handleGrantQuota = async (teacher) => {
    const confirm = await showConfirmDialog({
      title: "Thêm lượt tải?",
      text: `Cấp thêm 5 lượt tải/xem tài liệu cho ${teacher.name}.`,
      confirmText: "Cấp +5 lượt",
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = await grantDocumentAccessBonus(teacher.id, { packs: 1 });
      showSuccessToast(res?.message || "Đã cấp thêm 5 lượt.");
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Không cấp được lượt tải."
      );
    }
  };

  const handleDelete = async (teacher) => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.teacher_account,
      name: teacher?.name,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteTeacherAccount(teacher.id);
      showSuccessToast("Đã xóa tài khoản.");
      notifyChanged();
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không xóa được.");
    }
  };

  const openTeacherDetail = (teacher, tab = "account") => {
    setDetailInitialTab(tab);
    setDetailTeacherId(teacher.id);
  };

  if (!isOpen || !school) return null;

  return (
    <>
      <div className="fixed inset-0 z-[115] flex items-center justify-center p-3 sm:p-4">
        <div
          className="absolute inset-0 bg-gray-900/60 backdrop-blur-[2px]"
          onClick={onClose}
        />
        <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-white bg-slate-50 shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 bg-white px-5 py-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-700">
                <School className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold text-gray-900 sm:text-xl">
                  {school.partner_school_name}
                </h2>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1 font-semibold text-gray-700">
                    <Users className="h-3.5 w-3.5" />
                    {Number(school.teacher_count || totalRecords)} giáo viên
                  </span>
                  {school.school_level ? (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>{school.school_level}</span>
                    </>
                  ) : null}
                  {school.branch_name ? (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>{school.branch_name}</span>
                    </>
                  ) : null}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl p-2 text-gray-500 hover:bg-gray-100"
            >
              <X size={22} />
            </button>
          </div>

          {/* Search */}
          <div className="border-b border-gray-100 bg-white px-5 py-3 sm:px-6">
            <div className="relative max-w-md">
              <Search
                className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Tìm giáo viên theo tên, email..."
                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-3 text-sm font-medium text-gray-700 outline-none transition focus:border-red-500 focus:bg-white"
              />
            </div>
          </div>

          {/* Table */}
          <div className="min-h-0 flex-1 overflow-auto px-3 py-3 sm:px-5">
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="overflow-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-gray-50/90">
                    <tr className="text-left text-gray-500">
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                        Giáo viên
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                        Email
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                        Điện thoại
                      </th>
                      <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide">
                        Trạng thái
                      </th>
                      <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((emp) => (
                      <tr
                        key={emp.id}
                        onClick={() => openTeacherDetail(emp)}
                        className={`cursor-pointer border-t border-gray-100 transition-colors hover:bg-red-50/30 ${
                          openActionMenuId === emp.id ? "relative z-50" : "z-20"
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <img
                              src={
                                emp.avatar_url ||
                                "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                              }
                              className="h-10 w-10 shrink-0 rounded-full border object-cover"
                              alt=""
                            />
                            <div className="min-w-0">
                              <p className="truncate font-bold text-gray-900 hover:text-red-700">
                                {emp.name}
                              </p>
                              <p className="truncate text-xs text-gray-500">
                                {emp.position_display || "Giáo viên"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{emp.email || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{emp.phone || "—"}</td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
                              emp.is_active
                                ? "border border-green-200 bg-green-50 text-green-700"
                                : "border border-red-200 bg-red-50 text-red-700"
                            }`}
                          >
                            {emp.is_active ? "Đang hoạt động" : "Ngưng sử dụng"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <TeacherAccountRowActionMenu
                            teacher={emp}
                            isOpen={openActionMenuId === emp.id}
                            onOpenChange={(open) =>
                              setOpenActionMenuId(open ? emp.id : null)
                            }
                            onResetPassword={handleResetPassword}
                            onChangeDevice={setDeviceTeacher}
                            onGrantQuota={handleGrantQuota}
                            onViewDetail={(t) => openTeacherDetail(t)}
                            onEdit={(t) => onEdit?.(t.id)}
                            onDelete={handleDelete}
                          />
                        </td>
                      </tr>
                    ))}
                    {!loading && items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-12 text-center text-gray-500"
                        >
                          Không có giáo viên phù hợp.
                        </td>
                      </tr>
                    ) : null}
                    {loading && items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-12 text-center text-gray-500"
                        >
                          Đang tải...
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination
              totalRecords={totalRecords}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <TeacherDeviceManageModal
        isOpen={!!deviceTeacher}
        teacher={deviceTeacher}
        onClose={() => setDeviceTeacher(null)}
      />

      <TeacherAccountDetailModal
        isOpen={!!detailTeacherId}
        employeeId={detailTeacherId}
        initialTab={detailInitialTab}
        onClose={() => setDetailTeacherId(null)}
        onEdit={(id) => {
          setDetailTeacherId(null);
          onEdit?.(id);
        }}
        onDeleted={() => {
          setDetailTeacherId(null);
          notifyChanged();
        }}
      />
    </>
  );
};

export default TeachersBySchoolModal;
